<script lang="ts">
    import Fa from 'svelte-fa'
    import { faEdit, faTrashAlt, faCheck } from '@fortawesome/free-solid-svg-icons'
    import { createEventDispatcher, onMount } from 'svelte'
    import { ServiceOnQuotation } from '../../../modules/posApi/quotation/quotation.entity'
    import ZentitySelect from '../../_common/z/zentity-select.svelte'
    import {
        TaxInvoiceServiceEntity,
        TaxInvoiceServiceISSEntity,
        TaxInvoiceServiceValueEntity,
    } from '../../../modules/posApi/tax-invoices/tax-invoice-service.entity'
    import type { TaxParamEntity } from '../../../modules/posApi/tax-params/tax-param.entity'
    import { TaxParamNfseIssuerEnum } from '../../../modules/posApi/tax-params/tax-param.enum'
    import ZContainer from '../../_common/z/z-container.svelte'
    import ZContainerCol from '../../_common/z/z-container-col.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import ZinputBreak from '../../_common/z/zinput-break.svelte'
    import Zbutton from '../../_common/z/zbutton.svelte'
    const dispatch = createEventDispatcher()

    let className: string = ''
    export { className as class }
    export let item: Partial<TaxInvoiceServiceEntity> = undefined
    export let taxParams: TaxParamEntity
    // quem já desenha a linha do item por fora (a lista da nota) abre este componente só para editar
    export let startEditing = false

    // No emissor nacional a nota leva um único serviço, com os códigos fiscais vindos dos parâmetros: por
    // item só valem o serviço, a descrição (quando a nota tem um item só) e o valor. Os demais campos
    // ficam ocultos, porque não teriam efeito.
    $: isNacional = taxParams?.nfseIssuer === TaxParamNfseIssuerEnum.NACIONAL

    let step = 1
    let isNew = false
    async function editEntity() {
        step = 2
    }

    async function newEntity() {
        isNew = true
        item = new TaxInvoiceServiceEntity()
        item.iss = new TaxInvoiceServiceISSEntity()
        item.value = new TaxInvoiceServiceValueEntity()
        step = 2
    }

    async function cancelEntity() {
        item = undefined
        step = 1
        dispatch('cancel')
    }

    async function confirmEntity() {
        if (!item.value?.grossValue || !item.description) {
            window.error('Informe todas as opções antes de confirmar.')
            return
        }
        dispatch('added', item)
        if (isNew) item = undefined
        step = 1
        isNew = false
    }

    onMount(() => {
        if (!item) newEntity()
        else if (startEditing) step = 2
    })

    let lastService = null
    $: {
        if (isNew) {
            if (item && item.service && item?.service != lastService) {
                lastService = item?.service
                item.cnae = taxParams.nfseDefaultServiceCNAE
                item.nbs = taxParams.nfseDefaultServiceNBS
                item.codigoContribuinte = taxParams.nfseDefaultServiceCodigoContribuinte
                item.code = taxParams.nfseDefaultServiceCode
                item.description = item.service?.name || taxParams.nfseConfigSingleServiceDefaultDescription
                item.iss.taxRate = taxParams.nfseDefaultServiceTaxRate
                item.iss.taxSituation = taxParams.nfseDefaultServiceTaxSituation
                item.serviceId = item.service?.serviceId
                item.taxCode = taxParams.nfseDefaultServiceTaxCode
                item.value.grossValue = item.service?.price
                item.codigoContribuinte = taxParams.nfseDefaultServiceCodigoContribuinte
                item.codigoCidadeIncidencia = taxParams.nfseDefaultServiceCodigoCidadeIncidencia
                item.codigoServicoNacional = taxParams.nfseDefaultServiceCodigoServicoNacional
                item.ibscbs = taxParams.nfseDefaultServiceIBSCBS
            }
        }
    }

    function friendlyType(type: string) {
        if (type === 'service') return 'Serviço'
        if (type === 'package') return 'Pacote'
        if (type === 'product') return 'Produto'
        return type
    }
</script>

{#if item !== undefined}
    <div class={className}>
        {#if step === 1}
            <div class="row line">
                <div class="col nowrap">{item.description}</div>
                {#if !isNacional}
                    <div class="col-md-1">{item.code}</div>
                    <div class="col-md-1">{item.taxCode}</div>
                    <div class="col-md-1">{item.cnae}</div>
                    <div class="col-md-1">{item.iss?.taxRate}%</div>
                {/if}
                <div class="col-md-1 text-end">{window.api.utils.utils.toReal(item.value?.grossValue)}</div>
                <div class="col-md-2 text-end">
                    <button
                        class="btn btn-secondary btn-sm"
                        on:click={() => {
                            editEntity()
                        }}><Fa icon={faEdit} /> Editar</button
                    >
                </div>
            </div>
        {/if}
        {#if step === 2}
            <ZContainer>
                <ZContainerCol size={3}>
                    <!-- v2: o mesmo seletor do PDV, com busca em painel; sem ele cai no modo antigo -->
                    <ZentitySelect fonte="servico" label="Serviço" params={{ type: 'service', price: true }} bind:value={item.service} />
                </ZContainerCol>
                <Zinput
                    size={3}
                    type="text"
                    label="Descrição"
                    help={isNacional
                        ? 'Vai na nota quando ela tem um único serviço. Com mais de um, a nota sai com a descrição padrão dos parâmetros fiscais e o valor somado.'
                        : ''}
                    bind:value={item.description}
                ></Zinput>
                {#if !isNacional}
                    <Zinput size={3} type="number" label="ISS" bind:value={item.iss.taxRate}></Zinput>
                {/if}
                <Zinput size={3} type="number" label="Valor" bind:value={item.value.grossValue}></Zinput>
                {#if !isNacional}
                    <Zinput size={3} type="text" label="Cod Serviço" bind:value={item.code}></Zinput>
                    <Zinput size={3} type="text" label="Cod Tributação Mun" bind:value={item.taxCode}></Zinput>
                    <Zinput size={3} type="text" label="Cod Contribuinte" bind:value={item.codigoContribuinte}></Zinput>
                    <Zinput size={3} type="text" label="CNAE" bind:value={item.cnae}></Zinput>
                    <Zinput size={3} type="text" label="NBS" bind:value={item.nbs}></Zinput>
                    <Zinput size={3} type="text" label="Cod Cidade Incidência" bind:value={item.codigoCidadeIncidencia}></Zinput>
                    <Zinput size={3} type="text" label="Cod Serviço Nacional" bind:value={item.codigoServicoNacional}></Zinput>
                {/if}
                <!-- os botões fecham a última linha, só com ícone (o title dá o nome no hover) -->
                <ZContainerCol size={3} sizeM={12} flex={true} horizontalAlign="end">
                    <Zbutton class="me-2" on:click={cancelEntity} color="red" icon={faTrashAlt} title="Excluir"></Zbutton>
                    <Zbutton on:click={confirmEntity} color="green" icon={faCheck} title="Confirmar"></Zbutton>
                </ZContainerCol>
            </ZContainer>
            <!-- <div class="row mt-2 border-bottom-0 -form">
                <div class="col-12 col-xl ps-xl-1 pe-xl-2 mb-xl-0 space-bottom">
                    
                </div>
                <div class="col-12 col-xl ps-xl-1 pe-xl-2 mb-xl-0 space-bottom">
                    <div class="input-group input-group-sm">

                    </div>
                </div>
                <div class="col ps-xl-2 pe-xl-1 width-code">
                    <div class="input-group input-group-sm">
                        <div class="input-group-text">Cod Serviço</div>
                        <input type="text" bind:value={item.code} class="form-control form-control-sm" />
                    </div>
                </div>
                <div class="col col-md ps-xl-1 pe-xl-1 width-code">
                    <div class="input-group input-group-sm">
                        <div class="input-group-text">Cod Tribut.</div>
                        <input type="text" bind:value={item.taxCode} class="form-control form-control-sm" />
                    </div>
                </div>
                <div class="col col-md ps-xl-1 pe-xl-1 width-cnae">
                    <div class="input-group input-group-sm">
                        <div class="input-group-text">CNAE</div>
                        <input type="text" bind:value={item.cnae} class="form-control form-control-sm" />
                    </div>
                </div>
                <div class="col col-md ps-xl-1 pe-xl-1 width-iss">
                    <div class="input-group input-group-sm">
                        <div class="input-group-text">ISS</div>
                        <input type="number" bind:value={item.iss.taxRate} class="form-control form-control-sm" />
                    </div>
                </div>
                <div class="col text-end ps-xl-1 pe-xl-1 width-total">
                    <div class="input-group input-group-sm">
                        <div class="input-group-text">Valor</div>
                        <input type="number" bind:value={item.value.grossValue} class="form-control form-control-sm text-end" />
                    </div>
                </div>
                <div class="col-12 col-lg text-end ps-xl-1 btns">
                    <button class="btn btn-sm btn-warning" on:click={cancelEntity}><Fa icon={faTrashAlt} /><span class="h-full">Excluir</span></button>
                    <button class="btn btn-sm btn-primary" on:click={confirmEntity}><Fa icon={faCheck} /> Confirmar</button>
                </div>
            </div> -->
        {/if}
    </div>
{/if}

<style>
    .package_true {
        padding-left: 60px;
        display: flex;
        align-items: center;
    }
    .btn-package_true {
        opacity: 0.6;
    }
    .package_false {
        display: flex;
        align-items: center;
    }
    .btns {
        border: 0px solid red;
        max-width: 160px;
    }
    .width-iss {
        max-width: 80px;
    }
    @media (min-width: 1200px) {
        .width-user {
            max-width: 250px;
        }
        .width-vlr {
            max-width: 160px;
        }
    }

    @media (max-width: 1199px) {
        .space-bottom {
            margin-bottom: 8px;
        }
    }

    @media (max-width: 900px) {
        .btns {
            margin-top: 12px;
            max-width: unset;
        }
        .space-bottom {
            margin-bottom: unset;
        }
    }
</style>
